/* PopupNotificationsManager — modal-based editor for website_settings.popup_notifications.
 *
 * v2 (2026-09-25): replaces the right-side sheet. A top-level Website Manager
 * card opens this centered LIST modal (one line per notice); Edit/Add opens a
 * second FORM modal. Everything is OL config-driven — sport options come from
 * the org's active sports (SOT), nothing hardcoded. Each mutation persists via
 * PATCH /api/website-settings/{org}.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Bell, GripVertical, Trash2, Pencil, Plus, Loader2, QrCode, Upload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_POPUP_COLOR = '#5c0a14';

const blankItem = () => ({
  id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `n-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  enabled: true, label: '', url: '', sport: '',
  starts_at: '', ends_at: '', button_color: DEFAULT_POPUP_COLOR, order: 0, history: [],
});

const statusOf = (it) => {
  const md = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? '' : `${d.getMonth() + 1}/${d.getDate()}`; };
  if (!((it.url || '').trim())) return { text: 'No URL — hidden', cls: 'text-amber-300' };
  const s = (it.starts_at || '').trim(), e = (it.ends_at || '').trim();
  if (!s || !e) return { text: 'No schedule — hidden', cls: 'text-amber-300' };
  const now = Date.now(), sd = new Date(s).getTime(), ed = new Date(e).getTime();
  if (isNaN(sd) || isNaN(ed)) return { text: 'No schedule — hidden', cls: 'text-amber-300' };
  const range = `${md(s)}–${md(e)}`;
  if (now < sd) return { text: `Scheduled ${range}`, cls: 'text-sky-300' };
  if (now > ed) return { text: 'Expired', cls: 'text-amber-300' };
  return { text: `Live ${range}`, cls: 'text-emerald-300' };
};

export default function PopupNotificationsManager({ orgId, open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [editItem, setEditItem] = useState(null); // null = form modal closed
  const [editIsNew, setEditIsNew] = useState(false);
  const [qr, setQr] = useState(null);
  const [card, setCard] = useState(null);
  const [details, setDetails] = useState(null); // 3rd line on every notice
  const colorTimer = useRef(null);
  useEffect(() => () => clearTimeout(colorTimer.current), []);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/website-settings/${orgId}`, { withCredentials: true });
      setItems([...((data.popup_notifications?.items) || [])]);
      setQr({ ...(data.popup_notifications?.qr_fallback || {}) });
      setCard({ ...(data.popup_notifications?.card_bg || {}) });
      setDetails({ ...(data.popup_notifications?.details_line || {}) });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load popup notifications');
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => {
    if (!open || !orgId) return;
    load();
    axios.get(`${API}/api/organizations/${orgId}/sports`, { withCredentials: true })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data?.sports || []);
        setSports(list.map(s => ({ value: s.name || s.slug || s.id, label: s.name || s.slug })).filter(o => o.value));
      })
      .catch(() => setSports([]));
  }, [open, orgId, load]);

  const persist = useCallback(async (next) => {
    const ordered = next.map((it, i) => ({ ...it, order: i }));
    setItems(ordered);
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/api/website-settings/${orgId}`,
        { popup_notifications: { items: ordered } }, { withCredentials: true });
      setItems([...((data.popup_notifications?.items) || ordered)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      load();
    } finally { setSaving(false); }
  }, [orgId, load]);

  const toggleAt = (idx, v) => { const n = [...items]; n[idx] = { ...n[idx], enabled: v }; persist(n); };
  const removeAt = (idx) => { const n = [...items]; n.splice(idx, 1); persist(n); };
  const onDrop = (targetIdx) => {
    if (dragId == null) return;
    const from = items.findIndex(i => i.id === dragId); setDragId(null);
    if (from < 0 || from === targetIdx) return;
    const n = [...items]; const [m] = n.splice(from, 1); n.splice(targetIdx, 0, m); persist(n);
  };
  const saveEdit = (it) => {
    const idx = items.findIndex(x => x.id === it.id);
    const n = idx >= 0 ? items.map(x => x.id === it.id ? it : x) : [...items, it];
    persist(n); setEditItem(null);
  };

  // ── QR fallback (shown when no notice is active) ──────────────────────────
  const saveQr = useCallback(async (partial) => {
    const nextQr = { ...(qr || {}), ...partial };
    setQr(nextQr);
    setSaving(true);
    try {
      const ordered = items.map((it, i) => ({ ...it, order: i }));
      const { data } = await axios.patch(`${API}/api/website-settings/${orgId}`,
        { popup_notifications: { items: ordered, qr_fallback: partial } }, { withCredentials: true });
      setQr({ ...(data.popup_notifications?.qr_fallback || nextQr) });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed'); load();
    } finally { setSaving(false); }
  }, [qr, items, orgId, load]);

  // ── Details line (3rd line on every notice, e.g. "Press for Details") ────
  const saveDetails = useCallback(async (partial) => {
    setDetails(d => ({ ...(d || {}), ...partial }));
    setSaving(true);
    try {
      const ordered = items.map((it, i) => ({ ...it, order: i }));
      const { data } = await axios.patch(`${API}/api/website-settings/${orgId}`,
        { popup_notifications: { items: ordered, details_line: partial } }, { withCredentials: true });
      setDetails({ ...(data.popup_notifications?.details_line || {}) });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed'); load();
    } finally { setSaving(false); }
  }, [items, orgId, load]);

  const setMembersOnly = useCallback(async (v) => {
    setQr(q => ({ ...(q || {}), members_only: v }));
    setSaving(true);
    try {
      await axios.put(`${API}/api/website-visibility/${orgId}`,
        { gates: { popup_qr: v ? 'members' : 'public' } }, { withCredentials: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update visibility'); load();
    } finally { setSaving(false); }
  }, [orgId, load]);

  const uploadMedia = useCallback(async (kind, fileObj) => {
    if (!fileObj) return;
    const path = kind === 'pin' ? 'popup-qr-pin' : kind === 'card' ? 'popup-card' : 'popup-qr';
    const fd = new FormData(); fd.append('file', fileObj);
    setSaving(true);
    try {
      await axios.post(`${API}/api/website-settings/${orgId}/${path}`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(kind === 'pin' ? 'Pin graphic updated' : 'QR image updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setSaving(false); }
  }, [orgId, load]);

  const mediaSrc = (aid) => aid ? `${API}/api/wp-media/file/${orgId}/${aid}` : '';

  const removeCard = useCallback(async () => {
    if (!window.confirm('Remove the notice background graphic? Notices go back to the default paper look.')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/api/website-settings/${orgId}/popup-card`, { withCredentials: true });
      toast.success('Notice background removed'); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Remove failed'); }
    finally { setSaving(false); }
  }, [orgId, load]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[960px] bg-slate-900 border-slate-700 text-white" data-testid="popup-list-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Bell className="h-5 w-5 text-emerald-400" /> Popup Notifications
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Each enabled notice inside its date window shows as a rotating bulletin on the right of the sponsor bar. Drag to reorder — the first shows first.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-slate-700 divide-y divide-slate-800 max-h-[60vh] overflow-auto" data-testid="popup-list">
            {loading && <div className="px-3 py-6 text-center text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>}
            {!loading && items.length === 0 && <p className="px-3 py-6 text-center text-slate-500 text-sm" data-testid="popup-empty">No notices yet. Add one below.</p>}
            {!loading && items.map((it, idx) => (
              <div key={it.id} draggable
                onDragStart={() => setDragId(it.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(idx)}
                className={`flex items-center gap-3 px-3 h-11 ${dragId === it.id ? 'opacity-50' : ''}`}
                data-testid={`popup-row-${idx}`}>
                <GripVertical className="h-5 w-5 text-slate-500 cursor-grab flex-shrink-0" data-testid={`popup-drag-${idx}`} />
                <Switch checked={!!it.enabled} onCheckedChange={(v) => toggleAt(idx, v)}
                  className="scale-125 origin-left mr-1 data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-600"
                  data-testid={`popup-enabled-${idx}`} />
                <span className="h-5 w-5 rounded-sm border border-slate-600 flex-shrink-0" style={{ background: it.button_color || DEFAULT_POPUP_COLOR }} data-testid={`popup-swatch-${idx}`} />
                <span className="flex-1 min-w-0 truncate text-[15px] text-white" title={it.label} data-testid={`popup-label-${idx}`}>
                  {it.label || <span className="text-slate-500 italic">Untitled notice</span>}
                  {(it.reg_form_id || it.source === 'registration') && (
                    <span className="ml-2 text-[11px] text-cyan-400/80" data-testid={`popup-linked-${idx}`}>· From registration: {it.reg_form_name || 'linked'}</span>
                  )}
                </span>
                <span className="hidden sm:inline text-[13px] text-slate-400 capitalize w-24 truncate">{it.sport || 'all'}</span>
                {(() => { const st = statusOf(it); return (
                  <span className={`text-[12px] ${st.cls} w-44 text-right truncate`} data-testid={`popup-status-${idx}`}>{st.text}</span>
                ); })()}
                <button type="button" className="p-1 text-slate-400 hover:text-white" onClick={() => { setEditIsNew(false); setEditItem({ ...it }); }} data-testid={`popup-edit-${idx}`}><Pencil className="h-[18px] w-[18px]" /></button>
                {!(it.reg_form_id || it.source === 'registration') && (
                  <button type="button" className="p-1 text-slate-400 hover:text-red-400" onClick={() => removeAt(idx)} data-testid={`popup-delete-${idx}`}><Trash2 className="h-[18px] w-[18px]" /></button>
                )}
              </div>
            ))}
          </div>

          <div className="mt-3 rounded-md border border-slate-700 bg-slate-950/40 p-3" data-testid="popup-card-section">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[15px] font-medium text-white">Notice background</span>
              <span className="text-[12px] text-slate-500">one graphic behind every notice</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-14 w-24 rounded border border-slate-600 bg-white/5 flex items-center justify-center overflow-hidden flex-shrink-0 text-[11px] text-slate-400">
                {card?.media_attachment_id
                  ? <img src={mediaSrc(card.media_attachment_id)} alt="Notice background" className="h-full w-full object-cover" data-testid="popup-card-preview" />
                  : 'Default paper'}
              </div>
              <label className="text-[13px] text-emerald-400 hover:text-emerald-300 cursor-pointer inline-flex items-center gap-1" data-testid="popup-card-replace-label">
                <Upload className="h-3.5 w-3.5" /> {card?.media_attachment_id ? 'Replace' : 'Upload'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadMedia('card', e.target.files?.[0])} data-testid="popup-card-file" />
              </label>
              {card?.media_attachment_id && (
                <button type="button" className="text-[13px] text-red-400 hover:text-red-300" onClick={removeCard} data-testid="popup-card-remove">Remove</button>
              )}
            </div>
          </div>

          {details && (
          <div className="mt-3 rounded-md border border-slate-700 bg-slate-950/40 p-3" data-testid="popup-details-section">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[15px] font-medium text-white">Details line</span>
              <span className="text-[12px] text-slate-500">third line on every notice — leave blank to hide</span>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-[12px] text-slate-400">Text</Label>
                <Input defaultValue={details.text || ''} maxLength={60}
                  onBlur={(e) => { if (e.target.value.trim() !== (details.text || '')) saveDetails({ text: e.target.value }); }}
                  className="bg-slate-900 border-slate-600 h-9 text-sm mt-1 text-white" placeholder="Press for Details" data-testid="popup-details-text" />
              </div>
              <div>
                <Label className="text-[12px] text-slate-400">Colour</Label>
                <input type="color" value={details.color || '#c2410c'}
                  onChange={(e) => {
                    const c = e.target.value;
                    setDetails(d => ({ ...(d || {}), color: c }));
                    clearTimeout(colorTimer.current);
                    colorTimer.current = setTimeout(() => saveDetails({ color: c }), 600);
                  }}
                  className="mt-1 h-9 w-14 rounded border border-slate-600 bg-slate-900 cursor-pointer" data-testid="popup-details-color" />
              </div>
              <span className="mb-2 text-[12px] font-extrabold uppercase tracking-wider" style={{ color: details.color || '#c2410c' }} data-testid="popup-details-preview">
                {details.text || '(hidden)'}
              </span>
            </div>
          </div>
          )}

          {qr && (
          <div className="mt-3 rounded-md border border-slate-700 bg-slate-950/40 p-3" data-testid="popup-qr-section">
            <div className="flex items-center gap-2 mb-2">
              <QrCode className="h-4 w-4 text-emerald-400" />
              <span className="text-[15px] font-medium text-white">Sabres App QR</span>
              <span className="text-[12px] text-slate-500">shown when no notice is active</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <Switch checked={!!qr.enabled} onCheckedChange={(v) => saveQr({ enabled: v })}
                  className="scale-110 data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-600"
                  data-testid="popup-qr-enabled" />
                <span className="text-[13px] text-slate-300">Enable QR tile</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!qr.members_only} onCheckedChange={setMembersOnly}
                  className="scale-110 data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-600"
                  data-testid="popup-qr-members-only" />
                <span className="text-[13px] text-slate-300">Members only</span>
              </div>
              <div>
                <Label className="text-[12px] text-slate-400">Label</Label>
                <Input defaultValue={qr.label || ''} onBlur={(e) => { if (e.target.value !== (qr.label || '')) saveQr({ label: e.target.value }); }}
                  className="bg-slate-900 border-slate-600 h-9 text-sm mt-1 text-white" placeholder="Get the Sabres App" data-testid="popup-qr-label" />
              </div>
              <div>
                <Label className="text-[12px] text-slate-400">Link URL</Label>
                <Input defaultValue={qr.url || ''} onBlur={(e) => { if (e.target.value !== (qr.url || '')) saveQr({ url: e.target.value }); }}
                  className="bg-slate-900 border-slate-600 h-9 text-sm mt-1 text-white font-mono" placeholder="/members/app/" data-testid="popup-qr-url" />
              </div>
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded border border-slate-600 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                  {qr.qr_media_attachment_id
                    ? <img src={mediaSrc(qr.qr_media_attachment_id)} alt="QR" className="h-full w-full object-contain" data-testid="popup-qr-preview" />
                    : <QrCode className="h-6 w-6 text-slate-300" />}
                </div>
                <label className="text-[13px] text-emerald-400 hover:text-emerald-300 cursor-pointer inline-flex items-center gap-1" data-testid="popup-qr-replace-label">
                  <Upload className="h-3.5 w-3.5" /> {qr.qr_media_attachment_id ? 'Replace QR' : 'Upload QR'}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadMedia('qr', e.target.files?.[0])} data-testid="popup-qr-file" />
                </label>
              </div>
            </div>
          </div>
          )}

          <DialogFooter className="sm:justify-between">
            <Button variant="outline" size="sm" className="border-slate-600 text-slate-200"
              onClick={() => { setEditIsNew(true); setEditItem(blankItem()); }} data-testid="popup-add-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add notice
            </Button>
            <Button size="sm" variant="ghost" className="text-slate-300" onClick={() => onOpenChange(false)} data-testid="popup-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PopupEditModal item={editItem} isNew={editIsNew} sports={sports} onCancel={() => setEditItem(null)} onSave={saveEdit} />
    </>
  );
}

function PopupEditModal({ item, isNew, sports, onCancel, onSave }) {
  const [draft, setDraft] = useState(item);
  useEffect(() => { setDraft(item); }, [item]);
  if (!draft) return null;
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const isLinked = !!(draft.reg_form_id || draft.source === 'registration');

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-[720px] bg-slate-900 border-slate-700 text-white" data-testid="popup-edit-modal">
        <DialogHeader>
          <DialogTitle className="text-lg">{isNew ? 'Add notice' : 'Edit notice'}</DialogTitle>
        </DialogHeader>
        {isLinked && (
          <p className="text-[12px] text-cyan-400/80 -mt-2" data-testid="popup-form-linked-note">
            From registration: {draft.reg_form_name || 'linked form'}. Link &amp; sport are managed by the registration; the dates stay in sync with the form's open/close dates.
          </p>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[13px] text-slate-300">Label</Label>
            <Input value={draft.label || ''} onChange={(e) => set('label', e.target.value)}
              className="bg-slate-900 border-slate-600 h-10 text-sm mt-1 text-white" placeholder="Basketball Registration" data-testid="popup-form-label" />
          </div>
          <div>
            <Label className="text-[13px] text-slate-300">Sport (optional)</Label>
            <select value={draft.sport || ''} onChange={(e) => set('sport', e.target.value)} disabled={isLinked}
              className="w-full bg-slate-900 border border-slate-600 rounded-md h-10 text-sm mt-1 text-white px-2 capitalize disabled:opacity-60 disabled:cursor-not-allowed" data-testid="popup-form-sport">
              <option value="">All / none</option>
              {(sports || []).map(s => <option key={s.value} value={s.value} className="capitalize">{s.label}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-[13px] text-slate-300">Link URL</Label>
            <Input value={draft.url || ''} onChange={(e) => set('url', e.target.value)} disabled={isLinked} readOnly={isLinked}
              className="bg-slate-900 border-slate-600 h-10 text-sm mt-1 text-white font-mono disabled:opacity-60 disabled:cursor-not-allowed" placeholder="https://…" data-testid="popup-form-url" />
          </div>
          <div>
            <Label className="text-[13px] text-slate-300">Start {draft.enabled && <span className="text-red-400">*</span>}</Label>
            <Input type="datetime-local" value={(draft.starts_at || '').slice(0, 16)} onChange={(e) => set('starts_at', e.target.value)}
              className="bg-slate-900 border-slate-600 h-10 text-sm mt-1 text-white" data-testid="popup-form-start" />
            {draft.enabled && !draft.starts_at && <p className="text-[11px] text-amber-400 mt-1" data-testid="popup-form-start-hint">Required to show</p>}
          </div>
          <div>
            <Label className="text-[13px] text-slate-300">End {draft.enabled && <span className="text-red-400">*</span>}</Label>
            <Input type="datetime-local" value={(draft.ends_at || '').slice(0, 16)} onChange={(e) => set('ends_at', e.target.value)}
              className="bg-slate-900 border-slate-600 h-10 text-sm mt-1 text-white" data-testid="popup-form-end" />
            {draft.enabled && !draft.ends_at && <p className="text-[11px] text-amber-400 mt-1" data-testid="popup-form-end-hint">Required to show</p>}
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <Label className="text-[13px] text-slate-300">Button colour</Label>
            <input type="color" value={draft.button_color || DEFAULT_POPUP_COLOR} onChange={(e) => set('button_color', e.target.value)}
              className="h-9 w-12 bg-transparent border border-slate-600 rounded cursor-pointer" data-testid="popup-form-color" />
            <span className="text-[12px] text-slate-500 ml-3">Off-site links open in a new tab automatically; same-site links open in the same tab.</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" className="text-slate-300" onClick={onCancel} data-testid="popup-form-cancel">Cancel</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => onSave({ ...draft, label: (draft.label || '').trim() || 'Notice' })} data-testid="popup-form-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
